import React from 'react';
import reactUpdate from 'immutability-helper';
import _ from 'underscore';
import DataTestAttrsType from 'test/lib/cypress/DataTestAttrsType';
import FieldActionButtons from 'metadata/FieldActionButtons';
import PropTypes from 'prop-types';
import ProteinAlignmentActions from 'molbio/proteins/alignments/ProteinAlignmentActions';
import FormInput from 'ui-library/bs/FormInput';
import SortableTable from 'ui-library/SortableTable';
import {readableTime} from 'util/DateUtil';
import * as keyboard from 'util/Keyboard';
import * as modals from 'util/Modals';

class ProteinAlignmentPanelTable extends React.Component {
  static propTypes = {
    alignments: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        createdAt: PropTypes.string.isRequired,
      })
    ).isRequired,
    showActionButtons: PropTypes.bool.isRequired,
    closePanel: PropTypes.func.isRequired,
    dataTestAttrs: DataTestAttrsType,
  };

  UNSAFE_componentWillReceiveProps(newProps) {
    this.setState({
      rowUiState: this._mapAlignments(
        newProps.alignments,
        (alignment) => this.state.rowUiState[alignment.id] || this._emptyRowUiState(alignment)
      ),
    });
  }

  _mapAlignments = (alignments, func) =>
    _.object(
      // eslint-disable-next-line no-restricted-syntax
      _.pluck(alignments, 'id'),
      alignments.map((alignment) => func(alignment))
    );

  _emptyRowUiState = (alignment) => ({
    isEditing: false,
    inputValue: alignment.name,
  });

  _isNotEditingAnyRow = () => !Object.values(this.state.rowUiState).some((rowState) => rowState.isEditing);

  _handleClickRow = (alignment) => {
    if (this._isNotEditingAnyRow()) {
      ProteinAlignmentActions.open({proteinId: alignment.proteinId, alignmentId: alignment.id});
      this.props.closePanel();
    }
  };

  _deleteAlignmentHandler = (alignment) => (e) => {
    // Prevent row from being clicked
    e.stopPropagation();
    modals
      .confirm(
        'Confirm Delete',
        <span>
          Are you sure you want to delete the alignment <strong>{alignment.name}</strong>? This action is not
          reversible.
        </span>,
        'danger'
      )
      .done(() => {
        ProteinAlignmentActions.delete(alignment.proteinId, alignment.id);
      });
  };

  _getInputValue = (alignmentId) => this.state.rowUiState[alignmentId].inputValue;

  _getIsEditing = (alignmentId) => this.state.rowUiState[alignmentId].isEditing;

  _setRowUiState = (alignmentId, updateObject) => {
    const newState = reactUpdate(this.state, {
      rowUiState: {
        [alignmentId]: {$merge: updateObject},
      },
    });
    this.setState(newState);
  };

  _editHandler = (alignmentId) => (e) => {
    e.stopPropagation();
    this._setRowUiState(alignmentId, {isEditing: true});
  };

  _cancelHandler = (alignmentId, alignmentName) => (e) => {
    this._setRowUiState(alignmentId, {
      isEditing: false,
      inputValue: alignmentName,
    });
  };

  _completeEditing = (alignment) => {
    const name = this._getInputValue(alignment.id);
    ProteinAlignmentActions.rename(alignment.id, name);
    this._setRowUiState(alignment.id, {isEditing: false});
  };

  _completeEditingHandler = (alignment) => (e) => {
    this._completeEditing(alignment);
  };

  _isCompleteEnabled = (alignmentId) => Boolean(this._getInputValue(alignmentId));

  _onChangeHandler = (alignmentId) => (e) => {
    this._setRowUiState(alignmentId, {inputValue: e.target.value});
  };

  _keyDownHandler = (alignment) => (e) => {
    if (keyboard.isEnterKey(e)) {
      e.preventDefault();
      if (this._isCompleteEnabled(alignment.id)) {
        this._completeEditing(alignment);
      }
    } else if (keyboard.isEscapeKey(e)) {
      e.preventDefault();
      e.stopPropagation();
      this._setRowUiState(alignment.id, {isEditing: false});
    }
  };

  _handleOnFocus = (e) => {
    const input = e.target;
    input.setSelectionRange(0, input.value.length);
  };

  _renderNameColumn = (column, row) => {
    const alignmentId = row.id;
    if (this._getIsEditing(alignmentId)) {
      return (
        <FormInput
          autoFocus={true}
          onFocus={this._handleOnFocus}
          value={this._getInputValue(alignmentId)}
          onChange={this._onChangeHandler(alignmentId)}
          onKeyDown={this._keyDownHandler(row)}
        />
      );
    } else {
      return row.name;
    }
  };

  _renderActionButtonColumn = (column, row) => {
    const alignmentId = row.id;
    return (
      this.props.showActionButtons && (
        <FieldActionButtons
          isEditing={this._getIsEditing(alignmentId)}
          cancel={this._cancelHandler(alignmentId, row.name)}
          complete={this._completeEditingHandler(row)}
          isCompleteEnabled={this._isCompleteEnabled(alignmentId)}
          remove={this._deleteAlignmentHandler(row)}
          edit={this._editHandler(alignmentId)}
          className="alignmentPanel-actionButtons"
        />
      )
    );
  };

  state = {
    rowUiState: this._mapAlignments(this.props.alignments, this._emptyRowUiState),
  };

  render() {
    return (
      <SortableTable
        columns={[
          SortableTable.defaultColumn('Name', 'name', {render: this._renderNameColumn}),
          SortableTable.defaultColumn('Created', 'created', {
            render: (column, row) => readableTime(row.createdAt, {showTime: false}),
          }),
          {
            header: '',
            sortable: false,
            render: this._renderActionButtonColumn,
          },
        ]}
        rows={this.props.alignments}
        onClickRow={this._handleClickRow}
        clickable={this._isNotEditingAnyRow()}
        verticalAlignMiddle={true}
        className="u-small-spacing-top panel-container-table alignmentPanel-table"
        dataTestAttrs={this.props.dataTestAttrs}
      />
    );
  }
}

export default ProteinAlignmentPanelTable;
